import * as React from 'react'
import { Dialog } from 'react-aria-components'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { OverlayWrapper, Backdrop } from './overlay'
import { DialogTitle, DialogDescription, dialogPanelClass } from './dialog'

/**
 * AlertDialog — a modal that interrupts the user to confirm a consequential
 * action (delete, log out, discard). Composed from the `Overlay` primitives
 * (`OverlayWrapper` + `Backdrop`) and react-aria's `Dialog` with
 * `role="alertdialog"`, so focus is trapped and returned, the title labels the
 * dialog, and screen readers announce it as requiring action.
 *
 *   <AlertDialog
 *     trigger={<Button variant="danger">Delete</Button>}
 *     title="Delete account?"
 *     description="This permanently removes your data. This can't be undone."
 *     confirmLabel="Delete"
 *     destructive
 *     onConfirm={deleteAccount}
 *   />
 *
 * Pass a `trigger` element (its `onClick` is wired to open) for the simple case,
 * or drive it with `isOpen` + `onOpenChange`. By design an alert dialog can't be
 * dismissed by clicking outside or pressing Escape (`isDismissable` defaults
 * false, Escape disabled) — the user must choose Cancel or Confirm. The action
 * row is Cancel (outline) + Confirm (primary, or `danger` when `destructive`);
 * pass `footer` to replace it.
 */
export interface AlertDialogProps {
  /** Element that opens the dialog — its `onClick` is wired through. */
  trigger?: React.ReactElement
  /** Dialog heading; labels the dialog for assistive tech. */
  title: React.ReactNode
  /** Supporting copy under the title. */
  description?: React.ReactNode
  /** Leading icon (e.g. a warning glyph) shown above the title. */
  icon?: React.ReactNode
  /** Extra body content rendered below the description. */
  children?: React.ReactNode
  /** Confirm button label. */
  confirmLabel?: string
  /** Cancel button label. */
  cancelLabel?: string
  /** Style the confirm action as destructive (red). */
  destructive?: boolean
  /** Disable just the confirm action. */
  confirmDisabled?: boolean
  /** Focus the confirm button on open (default: focus stays on Cancel). */
  confirmAutoFocus?: boolean
  /** Called when Confirm is pressed (the dialog then closes). */
  onConfirm?: () => void
  /** Replace the default Cancel + Confirm action row. */
  footer?: React.ReactNode
  /** Controlled open state. */
  isOpen?: boolean
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean
  /** Called when the open state changes. */
  onOpenChange?: (isOpen: boolean) => void
  /** Allow closing by clicking the backdrop (default false). */
  isDismissable?: boolean
  /** Disable closing via the Escape key (default true). */
  isKeyboardDismissDisabled?: boolean
  /** Classes for the dialog panel. */
  className?: string
}

export const AlertDialog = ({
  trigger,
  title,
  description,
  icon,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  confirmDisabled = false,
  confirmAutoFocus = false,
  onConfirm,
  footer,
  isOpen,
  defaultOpen,
  onOpenChange,
  isDismissable = false,
  isKeyboardDismissDisabled = true,
  className,
}: AlertDialogProps) => {
  const descriptionId = React.useId()
  return (
    <OverlayWrapper
      trigger={trigger}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      <Backdrop
        isDismissable={isDismissable}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      >
        <Dialog
          role="alertdialog"
          // alertdialog should associate its supporting copy; RAC only auto-wires
          // the title, so point describedby at the description we render below.
          aria-describedby={description ? descriptionId : undefined}
          className={cn(dialogPanelClass, 'p-6', className)}
        >
          {({ close }) => (
            <>
              {icon && <div className="mb-4">{icon}</div>}
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <DialogDescription id={descriptionId} className="mt-2">
                  {description}
                </DialogDescription>
              )}
              {children && <div className="mt-3">{children}</div>}
              <div className="mt-6 flex justify-end gap-2">
                {footer ?? (
                  <>
                    <Button
                      type="button"
                      variant="primary"
                      appearance="outline"
                      onClick={close}
                    >
                      {cancelLabel}
                    </Button>
                    <Button
                      type="button"
                      variant={destructive ? 'danger' : 'primary'}
                      autoFocus={confirmAutoFocus}
                      disabled={confirmDisabled}
                      onClick={() => {
                        onConfirm?.()
                        close()
                      }}
                    >
                      {confirmLabel}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}
AlertDialog.displayName = 'AlertDialog'
